<template>
  <v-hover v-slot="{ hover }">
    <v-list-item :style="item.ack ? 'opacity: 0.6;' : ''" v-on="attachEventListener">
      <slot name="icon"></slot>

      <v-list-item-action v-if="hasActionListener()">
        <v-checkbox v-if="!item.ack" :input-value="false" :disabled="false" @click="action" />
        <tooltip v-else-if="item.ack" :text="confirmationText">
          <v-checkbox :input-value="true" :disabled="true" />
        </tooltip>
      </v-list-item-action>
      <v-list-item-content>
        <v-list-item-subtitle class="text-xs">
          <!-- Display due -->
          <v-icon v-if="showIcons" x-small class="mr-1">mdi-calendar-blank</v-icon>
          <span v-if="item.isOverdue" class="red--text">
            {{ $t("overdueSince", { date: detailedDateWithDay(item.start) }) }}
          </span>
          <span v-else>{{ item.start | detailedDateWithDay }}</span>
          <span v-if="!item.isAllDay">| {{ new Date(item.start) | formatHoursAndMinutes }} Uhr</span>
          <span v-if="!item.isAllDay && item.end"> - {{ new Date(item.end) | formatHoursAndMinutes }} Uhr</span>

          <tooltip v-if="item.recurringEventId" :text="$t('views.fleet.EventCreateDialog.recurrence')">
            <v-icon x-small>mdi-autorenew</v-icon>
          </tooltip>
        </v-list-item-subtitle>

        <v-list-item-title class="text-truncate" :class="item.ack ? 'text-decoration-line-through' : ''">
          <a v-if="hasOpenDetailListener()" v-text="item.summary" @click.stop="emitOpenDetail" />
          <span v-else v-text="item.summary" />
        </v-list-item-title>

        <v-list-item-subtitle v-if="!small && item.description" class="text-truncate text-xs">
          {{ item.description }}
        </v-list-item-subtitle>

        <v-list-item-subtitle class="text-truncate text-xs">
          <!-- Display vehicle on small-->
          <span v-if="small && vehicle">
            <v-icon x-small>mdi-car</v-icon>
            {{ vehicle.displayName || vehicle.numberplate || vehicle.id }}
          </span>

          <!-- Display location -->
          <span v-if="item.location">
            <v-icon small>mdi-map-marker</v-icon>
            {{ item.location }}
          </span>
        </v-list-item-subtitle>
        <v-list-item-subtitle v-if="item.rrule && isDebug"> {{ recurrenceDescription }} </v-list-item-subtitle>

        <!-- Display vehicle -->
        <refs-vehicle v-if="!small && vehicle" class="ml-n4" :item="vehicle" />
      </v-list-item-content>
      <v-list-item-action class="mx-0 px-0" v-if="confirmable">
        <tooltip :text="$t('common.BackendResourceEnum.click.tooltip.event')" v-if="isConfirmable">
          <v-flex>
            <v-btn
              v-if="hover || confirmablePersistant"
              :loading="item.loading"
              text
              color="success"
              elevation="0"
              small
              @click.stop="confirm"
            >
              {{ $t(`common.BackendResourceEnum.click.event`) }}
              <v-icon small class="pl-1"> mdi-check </v-icon>
            </v-btn>
          </v-flex>
        </tooltip>
        <event-card-acknowledgement-dialog
          ref="eventCardAcknowledgementDialog"
          :value="item"
          @confirmed="() => $emit('confirm', item)"
        />
      </v-list-item-action>
    </v-list-item>
  </v-hover>
</template>

<script lang="ts">
import { IEventUIDto } from "@/lib/dto/event/event-ui.dto";
import {
  detailedDate,
  detailedDateWithDay,
  formatHoursAndMinutes,
  simpleDate,
  simpleDoubleDigitDate
} from "@/lib/utility/date-helper";
import { GoToHelper } from "@/lib/utility/goToHelper";
import { ConfigModule } from "@/store/modules/config";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import { VehicleModule } from "@/store/modules/vehicle.store";
import { RRule } from "rrule";
import { Component, Prop, Ref, Vue } from "vue-property-decorator";
import EventCardAcknowledgementDialog from "../event/EventCardAcknowledgementDialog.vue";
import { IRefDetail } from "./RefsList.vue";
import RefsVehicle from "./RefsVehicle.vue";
import Tooltip from "./tooltip.vue";

@Component({
  components: { RefsVehicle, Tooltip, EventCardAcknowledgementDialog },
  filters: {
    simpleDate,
    simpleDoubleDigitDate,
    detailedDateWithDay,
    detailedDate,
    formatHoursAndMinutes
  }
})
export default class RefsEvent extends Vue implements IRefDetail<IEventUIDto> {
  @Prop()
  item!: IEventUIDto;

  @Prop({ default: false })
  small!: boolean;

  @Prop({ default: false })
  showIcons!: boolean;

  @Prop({ default: false })
  confirmable!: boolean;

  @Prop({ default: false })
  confirmablePersistant!: boolean;

  @Ref("eventCardAcknowledgementDialog")
  eventCardAcknowledgementDialog!: EventCardAcknowledgementDialog;

  hasClickListener() {
    return Boolean(this.$listeners && this.$listeners.click);
  }

  hasOpenDetailListener() {
    return Boolean(this.$listeners && this.$listeners.openDetail);
  }

  hasActionListener() {
    return Boolean(this.$listeners && this.$listeners.action);
  }

  get attachEventListener() {
    if (this.hasClickListener()) {
      return { click: this.click };
    }

    return undefined;
  }

  get recurrenceDescription() {
    if (this.item.rrule) {
      return RRule.fromString(this.item.rrule).toText();
    }

    return "";
  }

  get vehicle() {
    const vehicleId = this.item.vehicleId;
    if (!vehicleId) {
      return undefined;
    }

    const item = VehicleModule.maps.id.get(vehicleId)[0];
    if (item) {
      return item;
    }

    return VehicleModule.paginationList.find((v) => v.id === vehicleId);
  }

  get isDebug() {
    return ConfigModule.debug;
  }

  get ackUser() {
    const userId = this.item.ack?.by;
    if (userId) {
      return PartnerUserModule.maps.id.get(userId)[0];
    }

    return undefined;
  }

  get confirmationText() {
    return this.$t("views.fleet.FleetHomeVehicleTableEventsListItem.confirmed", {
      date: simpleDoubleDigitDate(this.item.ack?.at ?? ""),
      firstName: this.ackUser?.firstName,
      lastName: this.ackUser?.lastName
    });
  }

  get isConfirmable() {
    return Boolean(!this.item.ack);
  }

  simpleDoubleDigitDate(date: string) {
    return simpleDoubleDigitDate(date);
  }

  detailedDateWithDay(date: string) {
    return detailedDateWithDay(date);
  }

  emitOpenDetail() {
    this.$emit("openDetail", this.item);
  }

  click() {
    this.$emit("click", this.item);
  }

  action() {
    this.$emit("action", this.item);
  }

  confirm() {
    this.$log.debug("confirm");
    this.eventCardAcknowledgementDialog.open();
  }

  isOverdue(item: IEventUIDto) {
    return item.isOverdue;
  }

  goToDetail(openInNew = true) {
    if (!this.item.id) {
      throw new Error("Event has no id");
    }

    new GoToHelper(this.$router).goToEventDetail(this.item.partnerId, this.item.id, openInNew);
  }
}
</script>
